import useFilterHook from "hooks/useFilter";
import usePagination from "hooks/usePagination";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { TEST_RATE_COLUMNS_DATA, FILTER_DEFINITIONS } from "./config";
import { DEFAULT_TOTAL_RECORDS } from "utils/constants";
import FilterPanelMaster from "./FilterPanelMaster";
import Table from "components/Common/Table/index";
import { useMutation } from "@tanstack/react-query";
import {
  fetchLimsFranchiseCopyRates,
  updateLimsFranchiseCopyRates,
  updatePanelMasterSync,
} from "services/panelMaster";
import { useAlert } from "context/alert";
import Loader from "components/Common/loader";
import { FaSyncAlt } from "react-icons/fa";
import zoneLocalStorageId from "utils/zone";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PanelMaster = () => {
  const { showAlert } = useAlert();

  const hasAppliedPercentageMap = useRef({});

  const [selectedRows, setSelectedRows] = useState([]);
  const [editableRows, setEditableRows] = useState({});
  const [rate, setRate] = useState("");
  const [mrp, setMrp] = useState("");
  const [ratePercentage, setRatePercentage] = useState("");
  const [mrpPercentage, setMrpPercentage] = useState("");
  const [previousData, setPreviousData] = useState([]);
  const [isEditable, setIsEditable] = useState(false);
  const [error, setError] = useState(false);
  const [tableVersion, setTableVersion] = useState(0);
  const localRateMap = useRef({});
  const localRateMrpMap = useRef({});
  const manuallyEditedMap = useRef({});
  const manuallyEditedMrpMap = useRef({});
  const percentageRateMap = useRef({});

  const [transferRateToAllFranchise, setTransferRateToAllFranchise] =
    useState(false);

  const [isSuperFranchise] = useState(() =>
    Number(localStorage.getItem("is_super_franchise"))
  );
  const [isFranchise] = useState(() =>
    Number(localStorage.getItem("is_franchise_employee"))
  );
  const [isSubFranchise] = useState(() =>
    Number(localStorage.getItem("sub_franchise"))
  );

  const { currentPage, updateCurrentPage, updateTotalRecords } =
    usePagination();

  const { filters, updateFilter, updateMultipleFilters } = useFilterHook(
    FILTER_DEFINITIONS,
    updateCurrentPage
  );

  const queryFilters = {
    investigation_id: filters.investigation_id,
    zone_id: Number(filters.zone_id || zoneLocalStorageId()),
    is_active: filters.is_active ?? true,
    billing_category: filters.billing_category || undefined,
  };
  const shouldShowTable = Number(queryFilters.investigation_id) > 0 && queryFilters.zone_id > 0;

  const {
    data: testRateData = [],
    isFetching,
    isError: hasLoadError,
    refetch: refetchTestRate,
  } = useQuery(
    ["franchise-copy-rates", queryFilters],
    () => fetchLimsFranchiseCopyRates(currentPage, queryFilters),
    {
      keepPreviousData: false,
      staleTime: 5000,
      refetchOnWindowFocus: false,
      retry: 1,
      onSuccess: (res) => {
        updateTotalRecords(res.data.additional_data.total_count);
      },
      select: (res) => {
        const response = res?.data?.data || [];
        const defaults = res?.data?.default;

        if (response?.length && defaults?.length > 0) {
          const defaultRatesMap = defaults.reduce((acc, item) => {
            acc[item.investigation_id] = item.rate;
            return acc;
          }, {});
          return { ...res, data: { ...res.data, data: response.map((item, index) => ({
            ...item,
            franchise_type: item.parent_franchise_id ? "Child" : item.is_parent_franchise ? "Parent" : "Normal",
            rates: defaultRatesMap[item.investigation_id] || 0,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          })) } };
        } else {
          return { ...res, data: { ...res.data, data: response.map((x, index) => ({
            ...x,
            franchise_type: x.parent_franchise_id ? "Child" : x.is_parent_franchise ? "Parent" : "Normal",
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          })) } };
        }
      },
      onError: (res) => {
        console.error("Error fetching TestRates List:", res);
      },
      enabled: shouldShowTable,
    }
  );

  const resetEditor = useCallback(() => {
    localRateMap.current = {};
    localRateMrpMap.current = {};
    manuallyEditedMap.current = {};
    manuallyEditedMrpMap.current = {};
    percentageRateMap.current = {};
    hasAppliedPercentageMap.current = {};
    setSelectedRows([]);
    setPreviousData([]);
    setEditableRows({});
    setRate("");
    setMrp("");
    setRatePercentage("");
    setMrpPercentage("");
    setError(false);
    setTableVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    resetEditor();
  }, [queryFilters.investigation_id, queryFilters.zone_id, queryFilters.is_active,
      queryFilters.billing_category, resetEditor]);

  const onSuccess = async (response) => {
    const disabled = response?.data?.disabled_count || 0;
    showAlert(disabled
      ? `Rates saved. ${disabled} selected child rate(s) remain unavailable because their parent rate is disabled or zero.`
      : "Selected rates updated successfully!", "success");
    await refetchTestRate();
    resetEditor();
  };

  const onError = useCallback((failure) => {
    const detail = failure?.response?.data?.detail;
    showAlert(typeof detail === "string" ? detail : "Rates could not be saved. Your edits have been kept; please retry.", "danger");
  }, [showAlert]);

  const { mutate: updatePanelMasterMutate, isLoading: isSaving } = useMutation({
    mutationFn: updateLimsFranchiseCopyRates,
    onSuccess,
    onError,
  });

  useEffect(() => {
    // console.log("second useEffect");
    selectedRows.forEach((id) => {
      hasAppliedPercentageMap.current[id] = false;
    });
  }, [ratePercentage]);

  const { mutate: syncRates, isLoading: isSyncing } = useMutation({
    mutationFn: updatePanelMasterSync,
    onSuccess: async () => { await refetchTestRate(); resetEditor(); },
    onError,
  });
  const isFetchingData = isFetching || isSyncing;

  const sortedData = testRateData?.data?.data?.length
    ? selectedRows.length > 0
      ? [...testRateData.data.data].sort((a, b) => {
          const isSelectedA = selectedRows.includes(a.id);
          const isSelectedB = selectedRows.includes(b.id);

          if (isSelectedA && !isSelectedB) return -1;
          if (!isSelectedA && isSelectedB) return 1;
          return 0;
        })
      : [...testRateData.data.data]
    : [];

  const mergedData = [
    ...new Map(
      [...previousData, ...sortedData].map((item) => [item.id, item])
    ).values(),
  ];

  const toggleSelectAll = useCallback(
    (isChecked) => {
      const visibleRowIds = sortedData.map((row) => row.id);

      if (isChecked) {
        setSelectedRows(visibleRowIds);

        const updatedSelectedData = testRateData?.data?.data
          ? testRateData.data.data.filter((row) =>
              visibleRowIds.includes(row.id)
            )
          : [];

        setPreviousData((prev) => {
          const mergedData = [
            ...new Map(
              [...prev, ...updatedSelectedData].map((item) => [item.id, item])
            ).values(),
          ];
          return mergedData;
        });

        const allEditableRows = {};
        visibleRowIds.forEach((id) => {
          allEditableRows[id] = true;
          hasAppliedPercentageMap.current[id] = false;
        });
        setEditableRows(allEditableRows);
      } else {
        setSelectedRows([]);
        setEditableRows({});
        hasAppliedPercentageMap.current = {};
      }
    },
    [sortedData, testRateData]
  );

  const toggleSelectRow = useCallback(
    (id) => {
      const isSelected = selectedRows.includes(id);

      setSelectedRows((prev) =>
        isSelected ? prev.filter((rowId) => rowId !== id) : [...prev, id]
      );

      setEditableRows((prev) => ({ ...prev, [id]: !isSelected }));

      if (isSelected) {
        delete hasAppliedPercentageMap.current[id];
        delete manuallyEditedMap.current[id];
        delete manuallyEditedMrpMap.current[id];
        delete localRateMap.current[id];
        delete localRateMrpMap.current[id];

        setPreviousData((prev) => prev.filter((row) => row.id !== id));
      } else {
        hasAppliedPercentageMap.current[id] = false;

        const row = testRateData?.data?.data?.find((r) => r.id === id);
        if (row) {
          setPreviousData((prev) =>
            prev.some((r) => r.id === id) ? prev : [...prev, row]
          );
        }
      }
    },
    [selectedRows, testRateData]
  );

 const handleSave = () => {
  const idsToSave = selectedRows.filter((id) => editableRows[id]);

  if (isSaving || isFetchingData || !idsToSave.length) return;

  try {
    const payload = idsToSave.map((id) => {
      const originalRow =
        testRateData?.data?.data?.find((row) => row.id === id) ||
        mergedData.find((row) => row.id === id);

      if (!originalRow) {
        throw new Error("A selected row is no longer available. Refresh the list.");
      }

      const rawRate = localRateMap.current[id] ?? originalRow.rate;
      const rawMrp = localRateMrpMap.current[id] ?? originalRow.mrp_rate;
      const finalRate = Math.ceil(Number(rawRate));
      const finalMrpRate = Math.ceil(Number(rawMrp));

      if (
        rawRate === "" ||
        rawMrp === "" ||
        !Number.isFinite(finalRate) ||
        !Number.isFinite(finalMrpRate) ||
        finalRate < 0 ||
        finalMrpRate < 0 ||
        finalMrpRate < finalRate
      ) {
        throw new Error(
          `${originalRow.franchise_name}: enter a non-negative rate and MRP at least equal to the rate.`
        );
      }

      return {
        id: Number(originalRow.id),
        test_code: originalRow.test_code,
        created_by: Number(originalRow.created_by),
        is_active: Number(originalRow.is_active),
        investigation_id: Number(originalRow.investigation_id),
        test_name: originalRow.test_name,
        rate: finalRate,
        mrp_rate: finalMrpRate,
        zone_id: Number(originalRow.zone_id || zoneLocalStorageId()),
        franchise_id: Number(originalRow.franchise_id),
        status: "PENDING",
        transfer_all: false,
      };
    });

    console.log("saving ids:", payload.map((p) => p.id)); // remove after debugging
    updatePanelMasterMutate({ bulk_update_test_rate: payload });
  } catch (failure) {
    showAlert(failure.message, "danger");
  }
};

  const [localRateEditableMap, setLocalRateEditableMap] = useState({});

  const columns = useMemo(() => {
    return TEST_RATE_COLUMNS_DATA({
      localRateMap,
      localRateMrpMap,
      manuallyEditedMap,
      manuallyEditedMrpMap,
      percentageRateMap,
      filters,
      toggleSelectAll,
      isEditable,
      selectedRows,
      editableRows,
      setEditableRows,
      toggleSelectRow,
      mrp,
      rate,
      mrpPercentage,
      ratePercentage,
      data: testRateData?.data?.data || [],
      isSuperFranchise,
      isFranchise,
      isSubFranchise,
      hasAppliedPercentageMap,
      // error,
      setError,
      localRateEditableMap,
      setLocalRateEditableMap,
      mergedData,
    });
  }, [
    filters,
    selectedRows,
    testRateData,
    isEditable,
    editableRows,
    mrp,
    rate,
    mrpPercentage,
    ratePercentage,
    isSuperFranchise,
    isFranchise,
    isSubFranchise,
    hasAppliedPercentageMap,
    setError,
    localRateEditableMap,
    percentageRateMap,
    mergedData,
  ]);

  useEffect(() => {
    // console.log("third useEffect");

    if (
      !isFetching && // ← YE CHANGE KIYA HAI (isFetchingData ki jagah isFetching)
      testRateData?.data?.additional_data?.is_editable !== undefined
    ) {
      setIsEditable(testRateData.data.additional_data.is_editable);
    }
  }, [isFetching, testRateData?.data?.additional_data?.is_editable]);

  const isFranchiseEmployee = localStorage.getItem("is_franchise_employee");

  const exportToCSV = () => {
    if (!mergedData || mergedData.length === 0) {
      alert("No data to export");
      return;
    }

    let filteredColumns = columns.filter((col) => col.accessor !== "_select");

    if (isFranchiseEmployee === "1") {
      filteredColumns = filteredColumns.filter(
        (col) => col.accessor !== "rates"
      );
    }

    const headers = filteredColumns
      .map((col) => col.Header || col.accessor)
      .join(",");

    const csvContent = mergedData
      .map((row) =>
        filteredColumns
          .map((col) => {
            const value = row[col.accessor] ?? "";
            return typeof value === "string" &&
              (value.includes(",") || value.includes('"'))
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          })
          .join(",")
      )
      .join("\n");

    const fullCSV = headers + "\n" + csvContent;

    const blob = new Blob([fullCSV], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `table-data-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    if (!mergedData || mergedData.length === 0) {
      alert("No data to export");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(12);

    const lines = [
      "LIMS SPECIALITY LABORATORY PVT. LTD.",
      "Head Office : 12, Indra Biswas Road (Near Belgachia Tricon Park)",
      "Kolkata - 700037 (Near - Belgachia Post Office)",
      "Branch Office : Guru Govind Singh link path road, Dist. - Patna,",
      "P.O. - Azimabad, Bihar - 800009, (Opposite of Soalty Hotel,",
      "Champaran Meet shop 2nd floor)",
    ];

    let startY = 10;

    lines.forEach((line, index) => {
      const textWidth = doc.getTextWidth(line);
      doc.text(line, (pageWidth - textWidth) / 2, startY + index * 6);
    });

    const contentStartY = startY + lines.length * 6 + 5;

    let filteredColumns = columns.filter((col) => col.accessor !== "_select");

    if (isFranchiseEmployee === "1") {
      filteredColumns = filteredColumns.filter(
        (col) => col.accessor !== "rates"
      );
    }

    const tableColumn = filteredColumns.map(
      (col) => col.Header || col.accessor
    );
    const tableRows = mergedData.map((row) =>
      filteredColumns.map((col) => row[col.accessor] ?? "-")
    );

    autoTable(doc, {
      startY: contentStartY,
      head: [tableColumn],
      body: tableRows,
      styles: {
        fontSize: 8,
        cellWidth: "wrap",
      },
      headStyles: {
        fillColor: [0, 112, 192],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [249, 249, 249] },
      margin: { left: 10, right: 10 },
      theme: "grid",
    });

    doc.save(`table-data-${new Date().toISOString().split("T")[0]}.pdf`);
  };

  return (
    <div className="container-fluid mb-3">
      {isSaving && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            borderRadius: "8px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Loader isLoading={true} />
          </div>
        </div>
      )}
      <div className="box mb-4">
        <div className="boxHeader d-flex justify-content-between align-items-center">
          <h1>Lims Franchise Copy Rates</h1>
          {
            <div className="d-flex align-items-center">
              {testRateData?.data?.data?.length > 6 &&
                !isFetchingData &&
                shouldShowTable && (
                  <button
                    type="submit"
                    onClick={handleSave}
                    // disabled={error}
                    disabled={error || isSaving || isFetchingData || !selectedRows.length}
                    className="btn btn-primary me-2"
                  >
                    Save
                  </button>
                )}
              <div className="d-flex gap-2 me-4">
                <button
                  onClick={exportToCSV}
                  className="btn btn-success bg-green-600 hover:bg-green-700"
                >
                  Export CSV
                </button>
                <button
                  onClick={exportToPDF}
                  className="btn btn-danger bg-red-600 hover:bg-red-700"
                >
                  Export PDF
                </button>
              </div>
              {isSuperFranchise != 1 &&
                isSubFranchise != 1 &&
                isFranchise != 1 && (
                  <button
                    type="button"
                    onClick={() => syncRates()}
                    disabled={isSaving || isFetchingData || selectedRows.length > 0}
                    className="btn btn-outline-primary d-flex align-items-center"
                    title="Refresh"
                  >
                    <FaSyncAlt />
                  </button>
                )}
            </div>
          }
        </div>

        <div className="boxBody">
          <fieldset disabled={isSaving} className="left d-lg-flex flex-wrap align-items-center">
            <FilterPanelMaster
              updateFilter={updateFilter}
              filters={filters}
              updateMultipleFilters={updateMultipleFilters}
              mrp={mrp}
              rate={rate}
              ratePercentage={ratePercentage}
              mrpPercentage={mrpPercentage}
              setMrp={setMrp}
              setRate={setRate}
              setRatePercentage={setRatePercentage}
              setMrpPercentage={setMrpPercentage}
              isSuperFranchise={isSuperFranchise}
              isFranchise={isFranchise}
              isSubFranchise={isSubFranchise}
              transferRateToAllFranchise={transferRateToAllFranchise}
              setTransferRateToAllFranchise={setTransferRateToAllFranchise}
              editableRows={editableRows}
              hasAppliedPercentageMap={hasAppliedPercentageMap}
            />
          </fieldset>
          <hr />
          <p className="text-muted">Only selected franchises receive the entered rate and MRP. Parent rate changes can also raise child minimum rates; existing higher child rates are preserved unless selected.</p>
          {hasLoadError && <div className="alert alert-danger">Rates could not be loaded. Please refresh and try again.</div>}
          {!isFetchingData && shouldShowTable && !hasLoadError && (
            <>
              <div className="mt-3">
                <Table
                  key={tableVersion}
                  data={mergedData || []}
                  isLoading={isFetchingData}
                  tableColumns={columns}
                  showPagination={false}
                />
              </div>
              <div>
                <button
                  type="submit"
                  onClick={handleSave}
                  // disabled={error}
                  disabled={error || isSaving || isFetchingData || !selectedRows.length}
                  className="btn btn-primary mt-4"
                >
                  Save
                </button>
              </div>
            </>
          )}
          {isFetchingData && <Loader isLoading={true} />}
        </div>
      </div>
    </div>
  );
};

export default PanelMaster;
